import { usePlayer } from '../context/PlayerContext';
import { Button } from '../components/ui/button';
import { Slider } from '../components/ui/slider';
import { Play, Pause, SkipBack, SkipForward, Volume2, VolumeX, ListMusic, X } from 'lucide-react';
import { useState } from 'react';

export const MiniPlayer = () => {
  const { 
    currentTrack, 
    isPlaying, 
    togglePlay, 
    playNext, 
    playPrevious, 
    currentTime, 
    duration, 
    seek, 
    volume, 
    changeVolume,
    queue
  } = usePlayer();
  const [showQueue, setShowQueue] = useState(false);

  if (!currentTrack) return null;

  const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  return (
    <>
      {/* Queue Panel */}
      {showQueue && (
        <div className="fixed bottom-24 right-4 w-80 max-h-96 bg-card border border-border rounded-lg shadow-2xl overflow-hidden z-50" data-testid="queue-panel">
          <div className="flex items-center justify-between p-3 border-b border-border">
            <h3 className="font-heading font-bold text-sm uppercase">Queue</h3>
            <Button variant="ghost" size="icon" onClick={() => setShowQueue(false)}>
              <X className="w-4 h-4" />
            </Button>
          </div>
          <div className="max-h-72 overflow-y-auto hide-scrollbar">
            {queue.length === 0 ? (
              <p className="p-4 text-sm text-muted-foreground text-center">Queue is empty</p>
            ) : (
              queue.map((track, index) => (
                <div key={track.track_id} className="flex items-center gap-3 p-3 hover:bg-muted/30">
                  <span className="text-xs text-muted-foreground w-4">{index + 1}</span>
                  <div className="w-10 h-10 bg-muted rounded overflow-hidden flex-shrink-0">
                    {track.cover_url && <img src={track.cover_url} alt="" className="w-full h-full object-cover" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{track.title}</p>
                    <p className="text-xs text-muted-foreground truncate">{track.artist_name}</p>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* Mini Player Bar */}
      <div className="fixed bottom-0 left-0 right-0 z-40 glass border-t border-border" data-testid="mini-player">
        {/* Progress Bar */}
        <div 
          className="absolute top-0 left-0 h-1 bg-gradient-to-r from-primary to-secondary cursor-pointer"
          style={{ width: `${progress}%` }}
        />
        <div 
          className="absolute top-0 left-0 right-0 h-1 bg-muted/50 cursor-pointer"
          onClick={(e) => {
            const rect = e.currentTarget.getBoundingClientRect();
            const percent = (e.clientX - rect.left) / rect.width;
            seek(percent * duration);
          }}
        />

        <div className="max-w-[1800px] mx-auto px-4 py-3">
          <div className="flex items-center justify-between gap-4">
            {/* Track Info */}
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <div className="w-12 h-12 bg-muted rounded overflow-hidden flex-shrink-0">
                {currentTrack.cover_url ? (
                  <img src={currentTrack.cover_url} alt={currentTrack.title} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-primary/20 to-secondary/20" />
                )}
              </div>
              <div className="min-w-0">
                <p className="text-sm font-medium truncate" data-testid="player-track-title">{currentTrack.title}</p>
                <p className="text-xs text-muted-foreground truncate">{currentTrack.artist_name}</p>
              </div>
            </div>

            {/* Playback Controls */}
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="icon" onClick={playPrevious} data-testid="player-prev">
                <SkipBack className="w-5 h-5" />
              </Button>
              <Button 
                size="icon" 
                onClick={togglePlay}
                className="bg-white text-black hover:bg-white/90 w-10 h-10 rounded-full"
                data-testid="player-play-pause"
              >
                {isPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5 ml-0.5" />}
              </Button>
              <Button variant="ghost" size="icon" onClick={playNext} data-testid="player-next">
                <SkipForward className="w-5 h-5" />
              </Button>
            </div>

            {/* Time & Volume */}
            <div className="hidden sm:flex items-center gap-4 flex-1 justify-end">
              <span className="text-xs text-muted-foreground font-mono">
                {formatTime(currentTime)} / {formatTime(duration)}
              </span>
              
              <div className="flex items-center gap-2 w-32">
                <Button variant="ghost" size="icon" className="w-8 h-8" onClick={() => changeVolume(volume > 0 ? 0 : 0.7)}>
                  {volume === 0 ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
                </Button>
                <Slider
                  value={[volume * 100]}
                  max={100}
                  step={1}
                  onValueChange={([v]) => changeVolume(v / 100)}
                  className="w-20"
                />
              </div>

              <Button 
                variant="ghost" 
                size="icon" 
                onClick={() => setShowQueue(!showQueue)}
                className={showQueue ? 'text-secondary' : ''}
                data-testid="player-queue-toggle"
              >
                <ListMusic className="w-5 h-5" />
                {queue.length > 0 && (
                  <span className="absolute -top-1 -right-1 w-4 h-4 bg-secondary text-[10px] font-bold rounded-full flex items-center justify-center">
                    {queue.length}
                  </span>
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
